/** One circuit: the map, how complete it is, and who made it.
 *
 * The Tracks page is the scoreboard; this is what a card on it opens. Three
 * things are here that a card has no room for. The map at a size where a gap
 * is a place rather than a percentage, with the road, the walls and the
 * centre line as layers. The survey's own figures per side — metres driven,
 * metres missing, whether the side closes into a lap. And the history: whose
 * surveys are in this circuit and when each was merged, which is the credit
 * the people who drove it get.
 *
 * Credit is by display name only for a contributor who turned that on in
 * their profile. Everybody else is counted, dated and called "a contributor",
 * because the privacy page says a name is public where its owner chose and
 * nowhere else — and the page says so itself, so that somebody who expected
 * to see their name learns where the switch is.
 *
 * Everything is public and nothing needs an account. Two requests, neither
 * waiting on the other: the circuit with its geometry, and its history. A
 * history that fails costs the page its two lower cards and nothing else; a
 * circuit the service has never heard of is the ordinary not-found page.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError, getMeta, getTrack, getTrackHistory } from '@/lib/api';
import { count, dateTime, metres, percent, plural, relativeTime, shortDate } from '@/lib/format';
import { useIsAdmin } from '@/lib/session';
import type {
  ServiceMeta,
  SideCoverage,
  Track,
  TrackCorner,
  TrackHistory,
  TrackSection,
} from '@/lib/types';
import {
  Bar,
  Card,
  EmptyState,
  ErrorNote,
  Kicker,
  LoadingPanel,
  NotFound,
  Page,
  Spinner,
  Tag,
  trackStateTone,
} from '@/components/ui';
import { TrackOutline, hasGeometry } from '@/components/TrackOutline';
import { TrackMap } from '@/components/tracks/TrackMap';
import type { MapFocus } from '@/components/tracks/TrackMap';
import { pullRequestLabel } from '@/components/tracks/list';
import {
  contributorLabel,
  creditsFrom,
  firstSurveyedAt,
  mergeCredit,
  timelineFrom,
} from '@/components/tracks/history';
import type { TimelineEntry } from '@/components/tracks/history';

const MUTED = 'var(--color-neutral-500)';
const FAINT = 'var(--color-neutral-600)';

function Fact({ name, children }: { name: string; children: ReactNode }) {
  return (
    <>
      <span style={{ color: MUTED }}>{name}</span>
      <span style={{ textAlign: 'right' }}>{children}</span>
    </>
  );
}

function External({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer noopener" style={{ whiteSpace: 'nowrap' }}>
      {children}
    </a>
  );
}

interface SideProps {
  label: string;
  pct: number | null;
  side: SideCoverage | undefined;
}

/** One border: how much of it is surveyed, in the survey's own metres when
 *  the published document carries them. */
function Side({ label, pct, side }: SideProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div
        className="tabular-nums"
        style={{
          display: 'grid',
          gridTemplateColumns: '44px minmax(0, 1fr) auto',
          gap: 10,
          alignItems: 'center',
          fontSize: 13,
        }}
      >
        <span style={{ color: MUTED }}>{label}</span>
        <Bar value={pct ?? 0} label={`${label} border surveyed`} />
        <span>{percent(pct)}</span>
      </div>
      {side ? (
        <div className="tabular-nums" style={{ fontSize: 11.5, color: FAINT, paddingLeft: 54 }}>
          {count(side.surveyed_m)} m surveyed · {count(side.gap_m)} m missing ·{' '}
          {side.closed ? 'closes into a lap' : 'not yet a closed lap'}
        </div>
      ) : null}
    </div>
  );
}

function SurveyCard({ track, meta }: { track: Track; meta: ServiceMeta | null }) {
  const coverage = track.geometry?.coverage;
  const pr = pullRequestLabel(track.last_pr_url);
  const source =
    meta?.track_data_url && track.last_published && track.slug
      ? `${meta.track_data_url}/blob/main/tracks/${encodeURIComponent(track.slug)}.json`
      : null;

  return (
    <Card as="section" label="The survey" style={{ gap: 12 }}>
      <Kicker>the survey</Kicker>

      <Side label="left" pct={track.coverage.left} side={coverage?.L} />
      <Side label="right" pct={track.coverage.right} side={coverage?.R} />

      <div
        className="tabular-nums"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: '7px 16px',
          fontSize: 13,
        }}
      >
        <Fact name="Length">{metres(track.length_m)}</Fact>
        {coverage ? <Fact name="Road width measured">{percent(coverage.road_pct)}</Fact> : null}
        <Fact name="Runs">{count(track.runs)}</Fact>
        <Fact name="Contributors">{count(track.contributors)}</Fact>
        <Fact name="Published">
          {track.last_published ? (
            <span title={dateTime(track.last_published)}>{relativeTime(track.last_published)}</span>
          ) : (
            <span style={{ color: MUTED }}>not yet</span>
          )}
        </Fact>
        <Fact name="Waiting to merge">
          {count(track.pending)}
          {track.held > 0 ? ` · ${count(track.held)} held` : ''}
        </Fact>
        {pr ? (
          <Fact name="Last pull request">
            <External href={track.last_pr_url}>
              {pr}
              <span className="sr-only"> for {track.name}, on GitHub</span>
            </External>
          </Fact>
        ) : null}
      </div>

      {source ? (
        <p style={{ margin: 0, fontSize: 11.5, color: FAINT, textWrap: 'pretty' }}>
          The survey itself — every metre, its votes and its corner labels — is public domain
          data: <External href={source}>{track.slug}.json</External>.
        </p>
      ) : null}
    </Card>
  );
}

const DIRECTION: Record<'L' | 'R', string> = { L: 'left', R: 'right' };

interface CornersCardProps {
  corners: readonly TrackCorner[];
  sections: readonly TrackSection[];
  selected: number | null;
  onPick: (corner: TrackCorner) => void;
}

/** The corners somebody marked, in the order a lap meets them. Each one is a
 *  button: picking it takes the map there. */
function CornersCard({ corners, sections, selected, onPick }: CornersCardProps) {
  const named = corners.filter((corner) => corner.name).length;
  const namedSections = sections.filter((section) => section.name);

  return (
    <Card as="section" label="Corners" style={{ gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <Kicker>corners</Kicker>
        {corners.length > 0 ? (
          <span className="tabular-nums" style={{ fontSize: 12, color: MUTED }}>
            {count(corners.length)} marked · {count(named)} named
          </span>
        ) : null}
      </div>

      {corners.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: MUTED, textWrap: 'pretty' }}>
          Nobody has marked this circuit’s corners yet. They are marked by hand in the datalogger
          and travel with the survey, so the next contributor who marks them fills this in.
        </p>
      ) : (
        <ol
          style={{
            margin: 0,
            padding: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
            gap: 4,
          }}
        >
          {corners.map((corner) => {
            const picked = corner.n === selected;
            return (
              <li key={corner.n} style={{ listStyle: 'none', minWidth: 0 }}>
                <button
                  type="button"
                  className="btn btn-ghost tabular-nums"
                  aria-pressed={picked}
                  title={corner.note || undefined}
                  onClick={() => onPick(corner)}
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    gap: 8,
                    padding: '4px 8px',
                    fontSize: 13,
                    background: picked
                      ? 'color-mix(in srgb, var(--color-accent) 18%, transparent)'
                      : undefined,
                  }}
                >
                  <span style={{ color: 'var(--color-accent)', flex: 'none', minWidth: 26 }}>
                    T{corner.n}
                  </span>
                  <span
                    className="truncate"
                    style={{ color: corner.name ? undefined : FAINT, minWidth: 0 }}
                  >
                    {corner.name || 'unnamed'}
                  </span>
                  {corner.direction ? (
                    <span style={{ marginLeft: 'auto', fontSize: 11.5, color: MUTED, flex: 'none' }}>
                      {DIRECTION[corner.direction]}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ol>
      )}

      {namedSections.length > 0 ? (
        <p style={{ margin: 0, fontSize: 12.5, color: MUTED, textWrap: 'pretty' }}>
          Sections: {namedSections.map((section) => section.name).join(' · ')}
        </p>
      ) : null}

      {corners.length > 0 ? (
        <p style={{ margin: 0, fontSize: 11.5, color: FAINT, textWrap: 'pretty' }}>
          Corners are marked and named by hand, and a wrong name is corrected in the track-data
          repository. Pick one to find it on the map.
        </p>
      ) : null}
    </Card>
  );
}

function CreditCard({ history }: { history: TrackHistory }) {
  const credits = useMemo(() => creditsFrom(history.contributors), [history]);
  const first = firstSurveyedAt(history);

  return (
    <Card as="section" label="Who surveyed this circuit" style={{ gap: 10 }}>
      <Kicker>surveyed by</Kicker>

      {history.contributors.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: MUTED, textWrap: 'pretty' }}>
          Nobody’s survey of this circuit has been merged yet.
        </p>
      ) : (
        <div>
          {credits.named.map((contributor, index) => (
            <div
              key={`${contributor.name}-${index}`}
              className="tabular-nums"
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                gap: 12,
                alignItems: 'baseline',
                padding: '6px 0',
                fontSize: 13.5,
                background: 'var(--row-rule) no-repeat bottom / 100% 1px',
              }}
            >
              <span className="truncate" title={contributor.name ?? undefined}>
                {contributor.name}
              </span>
              <span style={{ color: 'var(--color-neutral-400)', fontSize: 12.5 }}>
                {count(contributor.metres)} m · {plural(contributor.uploads, 'survey')}
              </span>
            </div>
          ))}
          {credits.anonymous ? (
            <div
              className="tabular-nums"
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                gap: 12,
                alignItems: 'baseline',
                padding: '6px 0',
                fontSize: 13.5,
                color: MUTED,
              }}
            >
              <span>
                {credits.named.length > 0 ? 'and ' : ''}
                {plural(credits.anonymous.accounts, 'contributor')} not named
              </span>
              <span style={{ fontSize: 12.5 }}>
                {count(credits.anonymous.metres)} m · {plural(credits.anonymous.uploads, 'survey')}
              </span>
            </div>
          ) : null}
        </div>
      )}

      <p style={{ margin: 0, fontSize: 11.5, color: FAINT, textWrap: 'pretty' }}>
        {first ? `First surveyed ${shortDate(first)}. ` : ''}
        A name appears only if its owner asked for it — contributors turn track credit on under{' '}
        <Link to="/profile">Profile</Link>.
      </p>
    </Card>
  );
}

function TimelineRow({ entry, last }: { entry: TimelineEntry; last: boolean }) {
  const merged = entry.kind === 'merged';
  const dot = merged ? 'var(--color-accent)' : 'var(--color-neutral-600)';

  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '14px minmax(0, 1fr)',
        gap: 12,
        listStyle: 'none',
      }}
    >
      {/* The rail: a dot per entry, and a line down to the next one. */}
      <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <span
          style={{
            width: 9,
            height: 9,
            marginTop: 5,
            borderRadius: '50%',
            flex: 'none',
            background: merged ? dot : 'transparent',
            boxShadow: `inset 0 0 0 1.5px ${dot}`,
          }}
        />
        {last ? null : (
          <span style={{ width: 1, flex: 1, marginTop: 4, background: 'var(--color-divider)' }} />
        )}
      </div>

      <div style={{ paddingBottom: last ? 0 : 18, minWidth: 0 }}>
        <div
          className="tabular-nums"
          style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', alignItems: 'baseline' }}
        >
          <span style={{ fontSize: 13.5 }}>
            {merged ? (
              <>
                Merged <span title={dateTime(entry.at)}>{shortDate(entry.at)}</span>
              </>
            ) : (
              `${plural(entry.uploads.length, 'survey')} waiting for the next merge`
            )}
          </span>
          {merged ? (
            <span style={{ fontSize: 12.5, color: MUTED }}>
              {count(entry.metres)} m from {mergeCredit(entry).join(', ')}
            </span>
          ) : null}
          {merged && entry.pr_label ? (
            <span style={{ fontSize: 12.5, marginLeft: 'auto' }}>
              <External href={entry.pr_url}>
                {entry.pr_label}
                <span className="sr-only"> on GitHub</span> →
              </External>
            </span>
          ) : null}
        </div>

        <ul style={{ margin: '6px 0 0', padding: 0 }}>
          {entry.uploads.map((upload, index) => (
            <li
              key={`${upload.received_at}-${index}`}
              className="tabular-nums"
              style={{
                listStyle: 'none',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0 8px',
                fontSize: 12.5,
                color: MUTED,
                padding: '2px 0',
              }}
            >
              <span style={{ color: upload.name ? 'var(--color-neutral-300)' : MUTED }}>
                {contributorLabel(upload.name)}
              </span>
              <span>
                surveyed {count(upload.metres)} m · {dateTime(upload.received_at)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}

function TimelineCard({ history }: { history: TrackHistory }) {
  const entries = useMemo(() => timelineFrom(history), [history]);

  return (
    <Card as="section" label="Timeline of surveys and merges" style={{ gap: 12 }}>
      <Kicker>timeline</Kicker>

      {entries.length === 0 ? (
        <EmptyState title="Nothing merged yet" compact>
          A survey appears here when it is received, and again when the merge job has carried it
          into the published map.
        </EmptyState>
      ) : (
        <ol style={{ margin: 0, padding: 0 }}>
          {entries.map((entry, index) => (
            <TimelineRow
              key={`${entry.kind}-${entry.at}-${index}`}
              entry={entry}
              last={index === entries.length - 1}
            />
          ))}
        </ol>
      )}

      {history.truncated ? (
        <p style={{ margin: 0, fontSize: 11.5, color: FAINT }}>
          Only the most recent surveys are listed; the totals above count all of them.
        </p>
      ) : null}
    </Card>
  );
}

export default function TrackPage() {
  const isAdmin = useIsAdmin();
  const { officialId = '' } = useParams();

  const [track, setTrack] = useState<Track | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [missing, setMissing] = useState(false);
  const [history, setHistory] = useState<TrackHistory | null>(null);
  const [historyError, setHistoryError] = useState<unknown>(null);
  const [meta, setMeta] = useState<ServiceMeta | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [focus, setFocus] = useState<MapFocus | null>(null);
  const [selectedCorner, setSelectedCorner] = useState<number | null>(null);
  const [picks, setPicks] = useState(0);

  useEffect(() => {
    let live = true;
    setTrack(null);
    setFocus(null);
    setSelectedCorner(null);
    setError(null);
    setMissing(false);
    setHistory(null);
    setHistoryError(null);

    void getTrack(officialId).then(
      (value) => {
        if (live) setTrack(value);
      },
      (thrown: unknown) => {
        if (!live) return;
        if (thrown instanceof ApiError && thrown.status === 404) setMissing(true);
        else setError(thrown);
      },
    );
    void getTrackHistory(officialId).then(
      (value) => {
        if (live) setHistory(value);
      },
      (thrown: unknown) => {
        if (live) setHistoryError(thrown);
      },
    );

    return () => {
      live = false;
    };
  }, [officialId, attempt]);

  // The repository's address is chrome: without it the page loses one link.
  useEffect(() => {
    let live = true;
    void getMeta().then(
      (value) => {
        if (live) setMeta(value);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);

  if (missing) return <NotFound />;

  const back = (
    <Link to="/tracks" style={{ fontSize: 12.5, textDecoration: 'none' }}>
      ← All circuits
    </Link>
  );

  if (error) {
    return (
      <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {back}
        <ErrorNote
          error={error}
          title="The circuit could not be loaded."
          onRetry={() => setAttempt((value) => value + 1)}
        />
      </Page>
    );
  }

  if (!track) {
    return (
      <Page max={1300}>
        <LoadingPanel label="loading the circuit" />
      </Page>
    );
  }

  const drawn = hasGeometry(track.geometry);

  // Every pick is a new request, so choosing the corner the map was dragged
  // away from brings it back.
  const pickCorner = (corner: TrackCorner) => {
    setSelectedCorner(corner.n);
    setFocus({ x: corner.apex.x, z: corner.apex.z, key: picks + 1 });
    setPicks((value) => value + 1);
  };

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {back}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h1 style={{ fontSize: 24, margin: 0, textWrap: 'balance' }}>{track.name}</h1>
          <div className="tabular-nums" style={{ fontSize: 13, color: MUTED }}>
            {track.official_id} · {metres(track.length_m)} ·{' '}
            {plural(track.contributors, 'contributor')}
          </div>
        </div>
        {/* The editor works on a published survey, so that is when it is offered
            — and only to somebody the service would let in. */}
        {isAdmin && track.last_published ? (
          <Link
            to={`/admin/tracks/${encodeURIComponent(track.official_id)}/edit`}
            className="btn btn-secondary"
            style={{ fontSize: 13, textDecoration: 'none' }}
          >
            Edit this circuit
          </Link>
        ) : null}
        <Tag tone={trackStateTone(track.status)}>{track.status}</Tag>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <Card as="section" label="Map" style={{ gap: 10 }}>
            {drawn && track.geometry ? (
              <TrackMap
                geometry={track.geometry}
                title={`Map of ${track.name}`}
                focus={focus}
                selectedCorner={selectedCorner}
              />
            ) : (
              <>
                <TrackOutline state={track.status} strokeWidth={2} height={260} />
                <p style={{ margin: 0, fontSize: 13, color: MUTED, textWrap: 'pretty' }}>
                  Nothing is published for this circuit yet, so there is no map to draw.{' '}
                  {track.pending > 0
                    ? `${plural(track.pending, 'survey')} of it ${track.pending === 1 ? 'is' : 'are'} waiting for the merge job.`
                    : 'It appears once a survey of it has been merged.'}
                </p>
              </>
            )}
          </Card>

          {drawn && track.geometry ? (
            <CornersCard
              corners={track.geometry.corners ?? []}
              sections={track.geometry.sections ?? []}
              selected={selectedCorner}
              onPick={pickCorner}
            />
          ) : null}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <SurveyCard track={track} meta={meta} />
          {history ? <CreditCard history={history} /> : null}
        </div>
      </div>

      {historyError ? (
        <ErrorNote
          error={historyError}
          title="The circuit’s history could not be loaded."
          compact
          onRetry={() => setAttempt((value) => value + 1)}
        />
      ) : history ? (
        <TimelineCard history={history} />
      ) : (
        <Spinner label="loading the history" />
      )}
    </Page>
  );
}
